import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import TableFilters, { MANY_FILTERS_THRESHOLD } from './TableFilters';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faColumns,
    faFilter,
    faDownload,
    faFilePdf,
    faFileExcel,
    faFileCsv,
    faAt,
    faXmark,
    faMicrophone,
    faSpinner,
    faMagnifyingGlass,
    faGripVertical,
    faBookmark,
    faPlus,
    faCheck,
    faTrash,
    faRotateLeft,
    faPen,
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthProvider';
import { isAssistantEnabled } from '../Assistant/assistantSettings';
import useVoiceDictation, { isDictationSupported } from './useVoiceDictation';

// backdrop: dims the page behind the open dropdown (used by the filter panel, to focus on it).
const ToolbarBtn = ({ icon, label, active, disabled, dropdownWidth = 220, backdrop = false, children }) => {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState(null);
    const ref = useRef(null);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (ref.current?.contains(e.target)) return;
            if (dropdownRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // Rendered through a portal (position: fixed, computed here) instead of
    // absolutely inside the toolbar — a plain absolute dropdown gets clipped by
    // any ancestor with overflow:hidden (e.g. the rounded-corner accordion card
    // this table can sit in), no matter how high its z-index is. Flips above the
    // button when there isn't enough room underneath, same as InputProduct/ImprovedSelect.
    const updatePosition = useCallback(() => {
        if (!ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        const isRtl = document.documentElement.dir === 'rtl';
        const PREFERRED_HEIGHT = 420;
        const MARGIN = 8;
        const spaceBelow = window.innerHeight - rect.bottom - MARGIN;
        const spaceAbove = rect.top - MARGIN;
        const openUpward = spaceBelow < PREFERRED_HEIGHT && spaceAbove > spaceBelow;
        // Aligned on the button's end edge, then clamped inside the viewport — on a phone
        // the buttons sit on the left of a scrollable strip, so a right-aligned 220px+
        // dropdown would otherwise hang off the left edge of the screen.
        const width = Math.min(dropdownWidth, window.innerWidth - MARGIN * 2);
        const preferredLeft = isRtl ? rect.left : rect.right - width;
        const left = Math.min(Math.max(MARGIN, preferredLeft), window.innerWidth - width - MARGIN);
        setPos({
            top: openUpward ? undefined : rect.bottom + 6,
            bottom: openUpward ? window.innerHeight - rect.top + 6 : undefined,
            left,
            width,
            maxHeight: Math.max(200, openUpward ? spaceAbove : spaceBelow),
        });
    }, [dropdownWidth]);

    useEffect(() => {
        if (!open) return;
        updatePosition();
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);
        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [open, updatePosition]);

    return (
        <div ref={ref} style={{ position: 'relative' }}>
            <button
                type="button"
                disabled={disabled}
                onClick={() => !disabled && setOpen(o => !o)}
                className={`toolbar-btn${active ? ' toolbar-btn--active' : ''}${open ? ' is-open' : ''}`}
                aria-label={label}
                data-tooltip-id={open ? undefined : 'tooltip'}
                data-tooltip-content={label}
                style={{
                    // icon-only square (Vercel / Linear style): label in the tooltip, gray fill on
                    // hover (Table.scss); an applied filter / view: blue icon + dot
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    padding: 0,
                    borderRadius: 8,
                    border: 'none',
                    background: 'transparent',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    color: active ? '#2563eb' : '#64748b',
                    opacity: disabled ? 0.45 : 1,
                    userSelect: 'none',
                    outline: 'none',
                }}
            >
                <FontAwesomeIcon icon={icon} style={{ fontSize: 13.5 }} />
                {active && <span className="toolbar-btn__dot" />}
            </button>

            {open && pos && backdrop && createPortal(
                // Clicking it closes the dropdown through the outside-mousedown handler above.
                <div className="toolbar-backdrop" />,
                document.body
            )}

            {open && pos && createPortal(
                <div
                    ref={dropdownRef}
                    className="toolbar-dropdown"
                    style={{
                        position: 'fixed',
                        top: pos.top,
                        bottom: pos.bottom,
                        left: pos.left,
                        zIndex: 9999,
                        background: '#fff',
                        border: '1px solid #e8e8e8',
                        borderRadius: 12,
                        boxShadow: '0 12px 32px rgba(0,0,0,0.10)',
                        width: pos.width,
                        maxHeight: pos.maxHeight,
                        overflowY: 'auto',
                    }}
                    onMouseDown={e => e.stopPropagation()}
                >
                    {/* children may be a function receiving close(), for a close button inside */}
                    {typeof children === 'function' ? children(() => setOpen(false)) : children}
                </div>,
                document.body
            )}
        </div>
    );
};

const ColRow = ({ col, visibleColumns, setVisibleColumns, isDragging, isDragOver, onDragHandlers }) => {
    const [hover, setHover] = useState(false);
    return (
        <div
            {...onDragHandlers}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '6px 8px', borderRadius: 7,
                fontSize: 13,
                background: isDragOver ? '#eff6ff' : (hover ? '#f9fafb' : 'transparent'),
                opacity: isDragging ? 0.4 : 1,
                marginBottom: 1, userSelect: 'none',
                WebkitUserDrag: 'element',
            }}
        >
            <span style={{ color: '#c7cbd1', cursor: 'grab', display: 'flex', fontSize: 11 }}>
                <FontAwesomeIcon icon={faGripVertical} />
            </span>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', flex: 1, margin: 0 }}>
                <input
                    type="checkbox"
                    className="form-check-input-checkbox"
                    checked={col.visible}
                    onChange={() => setVisibleColumns(
                        visibleColumns.map(c => c.__key === col.__key ? { ...c, visible: !c.visible } : c)
                    )}
                />
                <span style={{ color: col.visible ? '#111827' : '#9ca3af', fontWeight: col.visible ? 500 : 400 }}>
                    {col.label || col.Header}
                </span>
            </label>
        </div>
    );
};

const ColumnsList = ({ visibleColumns, setVisibleColumns }) => {
    const { t } = useTranslation();
    const [draggedKey, setDraggedKey] = useState(null);
    const [dragOverKey, setDragOverKey] = useState(null);
    const reorderableColumns = visibleColumns.filter(col => col.accessor && col.Header);

    const handleDrop = (targetKey) => {
        if (!draggedKey || draggedKey === targetKey) { setDraggedKey(null); setDragOverKey(null); return; }
        const from = visibleColumns.findIndex(c => c.__key === draggedKey);
        const to = visibleColumns.findIndex(c => c.__key === targetKey);
        if (from === -1 || to === -1) { setDraggedKey(null); setDragOverKey(null); return; }
        const next = [...visibleColumns];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        setVisibleColumns(next);
        setDraggedKey(null);
        setDragOverKey(null);
    };

    return (
        <>
            {reorderableColumns.map(col => (
                <ColRow
                    key={col.__key}
                    col={col}
                    visibleColumns={visibleColumns}
                    setVisibleColumns={setVisibleColumns}
                    isDragging={draggedKey === col.__key}
                    isDragOver={dragOverKey === col.__key && draggedKey !== col.__key}
                    onDragHandlers={{
                        draggable: true,
                        onDragStart: (e) => {
                            e.dataTransfer.effectAllowed = 'move';
                            // Some browsers (notably Firefox) abort the drag entirely if
                            // setData isn't called during dragstart — the payload itself
                            // isn't used, reordering is driven by the draggedKey state below.
                            e.dataTransfer.setData('text/plain', col.__key);
                            setDraggedKey(col.__key);
                        },
                        onDragEnter: () => draggedKey && setDragOverKey(col.__key),
                        onDragOver: (e) => e.preventDefault(),
                        onDrop: (e) => { e.preventDefault(); handleDrop(col.__key); },
                        onDragEnd: () => { setDraggedKey(null); setDragOverKey(null); },
                    }}
                />
            ))}
            {reorderableColumns.length === 0 && (
                <p style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic', paddingLeft: 8 }}>{t('No columns')}</p>
            )}
        </>
    );
};

const ViewRow = ({ view, active, onApply, onDelete, onRename }) => {
    const { t } = useTranslation();
    const [hover, setHover] = useState(false);
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [editing, setEditing] = useState(false);
    const [nameDraft, setNameDraft] = useState(view.name);
    const [renaming, setRenaming] = useState(false);

    const saveRename = async () => {
        const trimmed = nameDraft.trim();
        if (!trimmed || trimmed === view.name) { setEditing(false); return; }
        setRenaming(true);
        try {
            await onRename(view.id, trimmed);
            setEditing(false);
        } finally {
            setRenaming(false);
        }
    };

    if (editing) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 4px' }}>
                <input
                    autoFocus
                    type="text"
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') saveRename(); if (e.key === 'Escape') setEditing(false); }}
                    disabled={renaming}
                    style={{
                        flex: 1, minWidth: 0, fontSize: 13, padding: '6px 8px',
                        border: '1px solid #6366f1', borderRadius: 6, outline: 'none',
                    }}
                />
                <button
                    type="button"
                    onClick={saveRename}
                    disabled={!nameDraft.trim() || renaming}
                    style={{
                        border: 'none', borderRadius: 6, padding: '6px 8px', flexShrink: 0,
                        background: '#4f46e5', color: '#fff',
                        cursor: (!nameDraft.trim() || renaming) ? 'not-allowed' : 'pointer',
                        opacity: (!nameDraft.trim() || renaming) ? 0.5 : 1,
                    }}
                >
                    <FontAwesomeIcon icon={faCheck} style={{ fontSize: 11 }} />
                </button>
            </div>
        );
    }

    return (
        <div
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => { setHover(false); setConfirmingDelete(false); }}
            style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '2px 4px', borderRadius: 7,
                background: active ? '#eef2ff' : (hover ? '#f9fafb' : 'transparent'),
            }}
        >
            <button
                type="button"
                onClick={() => onApply(view)}
                title={view.name}
                style={{
                    flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8,
                    background: 'none', border: 'none', padding: '7px 6px', textAlign: 'left',
                    fontSize: 13, fontWeight: active ? 600 : 500,
                    color: active ? '#4f46e5' : '#374151', cursor: 'pointer',
                }}
            >
                <FontAwesomeIcon icon={faBookmark} style={{ fontSize: 11, color: active ? '#6366f1' : '#c7cbd1', flexShrink: 0 }} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{view.name}</span>
            </button>
            {(hover || confirmingDelete) && (
                confirmingDelete ? (
                    <button
                        type="button"
                        disabled={deleting}
                        onClick={async (e) => {
                            e.stopPropagation();
                            setDeleting(true);
                            try { await onDelete(view.id); } finally { setDeleting(false); }
                        }}
                        style={{
                            border: 'none', background: 'none', color: '#dc2626',
                            fontSize: 11, fontWeight: 700, cursor: deleting ? 'wait' : 'pointer',
                            padding: '4px 6px', whiteSpace: 'nowrap', flexShrink: 0,
                        }}
                    >
                        {t('table_views.delete_confirm')}
                    </button>
                ) : (
                    <>
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setNameDraft(view.name); setEditing(true); }}
                            title={t('table_views.rename_tooltip')}
                            style={{
                                border: 'none', background: 'none', color: '#9ca3af', cursor: 'pointer',
                                padding: '4px 6px', display: 'flex', flexShrink: 0,
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.color = '#4f46e5'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = '#9ca3af'; }}
                        >
                            <FontAwesomeIcon icon={faPen} style={{ fontSize: 11 }} />
                        </button>
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setConfirmingDelete(true); }}
                            title={t('table_views.delete_tooltip')}
                            style={{
                                border: 'none', background: 'none', color: '#9ca3af', cursor: 'pointer',
                                padding: '4px 6px', display: 'flex', flexShrink: 0,
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.color = '#dc2626'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = '#9ca3af'; }}
                        >
                            <FontAwesomeIcon icon={faTrash} style={{ fontSize: 11 }} />
                        </button>
                    </>
                )
            )}
        </div>
    );
};

const ViewsPanel = ({ views, activeViewId, viewsLoading, onApplyView, onCreateView, onDeleteView, onResetView, onRenameView }) => {
    const { t } = useTranslation();
    const [creating, setCreating] = useState(false);
    const [name, setName] = useState('');
    const [saving, setSaving] = useState(false);
    const isDefault = !activeViewId;

    const handleCreate = async () => {
        if (!name.trim() || saving) return;
        setSaving(true);
        try {
            await onCreateView(name.trim());
            setName('');
            setCreating(false);
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <button
                type="button"
                onClick={onResetView}
                style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    width: '100%', padding: '7px 6px', marginBottom: 4,
                    background: isDefault ? '#eef2ff' : 'none', border: 'none', cursor: 'pointer', borderRadius: 7,
                    fontSize: 13, fontWeight: isDefault ? 600 : 500, color: isDefault ? '#4f46e5' : '#374151', textAlign: 'left',
                }}
                onMouseEnter={(e) => { if (!isDefault) e.currentTarget.style.background = '#f9fafb'; }}
                onMouseLeave={(e) => { if (!isDefault) e.currentTarget.style.background = 'none'; }}
            >
                <FontAwesomeIcon icon={faRotateLeft} style={{ fontSize: 11, color: isDefault ? '#6366f1' : '#9ca3af' }} />
                {t('table_views.default')}
            </button>

            {(viewsLoading || views.length > 0) && (
                <>
                    <div style={{ height: 1, background: '#f0f1f3', margin: '4px 0 8px' }} />
                    {viewsLoading ? (
                        <p style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic', padding: '4px 6px' }}>{t('Loading')}…</p>
                    ) : (
                        views.map(view => (
                            <ViewRow
                                key={view.id}
                                view={view}
                                active={view.id === activeViewId}
                                onApply={onApplyView}
                                onDelete={onDeleteView}
                                onRename={onRenameView}
                            />
                        ))
                    )}
                </>
            )}

            <div style={{ height: 1, background: '#f0f1f3', margin: '8px 0' }} />

            {creating ? (
                <div style={{ padding: '2px 4px', display: 'flex', gap: 6 }}>
                    <input
                        autoFocus
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') setCreating(false); }}
                        placeholder={t('table_views.name_placeholder')}
                        style={{
                            flex: 1, minWidth: 0, fontSize: 12.5, padding: '6px 8px',
                            border: '1px solid #e5e7eb', borderRadius: 6, outline: 'none',
                        }}
                    />
                    <button
                        type="button"
                        onClick={handleCreate}
                        disabled={!name.trim() || saving}
                        style={{
                            border: 'none', borderRadius: 6, padding: '0 10px', flexShrink: 0,
                            background: '#4f46e5', color: '#fff', fontSize: 12, fontWeight: 600,
                            cursor: (!name.trim() || saving) ? 'not-allowed' : 'pointer',
                            opacity: (!name.trim() || saving) ? 0.5 : 1,
                        }}
                    >
                        <FontAwesomeIcon icon={faCheck} />
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => setCreating(true)}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        width: '100%', padding: '7px 8px',
                        background: 'none', border: 'none', cursor: 'pointer', borderRadius: 7,
                        fontSize: 13, fontWeight: 500, color: '#4f46e5', textAlign: 'left',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#f9fafb'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                >
                    <FontAwesomeIcon icon={faPlus} style={{ fontSize: 11 }} />
                    {t('table_views.create')}
                </button>
            )}
        </>
    );
};

const ExportRow = ({ icon, color, label, onClick }) => {
    const [hover, setHover] = useState(false);
    return (
        <button
            type="button"
            onClick={onClick}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
                display: 'flex', alignItems: 'center', gap: 10,
                width: '100%', padding: '8px 16px',
                background: hover ? '#f9fafb' : 'transparent',
                border: 'none', cursor: 'pointer',
                fontSize: 13, color: '#374151', textAlign: 'left',
            }}
        >
            <FontAwesomeIcon icon={icon} style={{ color, width: 16, fontSize: 14 }} />
            {label}
        </button>
    );
};

const TableSearch = ({
    value,
    onChange,
    columns,
    onFilter,
    filters,
    visibleColumns,
    setVisibleColumns,
    onResetColumns,
    onExport,
    onReset,
    placeholder = "Search...",
    loadedCount = 0,
    totalElements = 0,
    showColumnsButton = true,
    showExportButton = true,
    // no cap by default: the field stretches up to the buttons, so the whole bar reads as the search
    // (a page can still cap it, e.g. InventaireBuilder)
    searchMaxWidth,
    viewToggle,
    toolbarExtra,
    counterExtra,
    autoFocusSearch = false,
    // mic button in the field (dictation through our voice service) — opt-in per listing
    voiceSearch = false,
    showViewsButton = true,
    views = [],
    viewsLoading = false,
    activeViewId,
    onApplyView,
    onCreateView,
    onDeleteView,
    onResetView,
    onRenameView,
}) => {
    const { t } = useTranslation();
    const hasActiveFilters = filters && Object.values(filters).some(v => v !== '' && v != null);
    const filterableCount = columns.filter(col => col.filterable?.component).length;

    // The result count sits inside the search field, on the right — its measured width
    // (plus the clear button when there's a value) is reserved as the input's right padding
    // so typed text never runs under it.
    const countRef = useRef(null);
    const [countWidth, setCountWidth] = useState(0);
    useEffect(() => {
        if (countRef.current) setCountWidth(countRef.current.offsetWidth);
    }, [loadedCount, totalElements, t]);
    const clearBtnSpace = value ? 30 : 0;

    // ── voice search ── the transcript replaces the search text (and runs it). Needs Saydalani AI
    // on, as the transcription endpoint does; hidden where the browser has no mic access.
    const { settings } = useAuth();
    const voiceEnabled = voiceSearch && isDictationSupported && isAssistantEnabled(settings);
    const dictation = useVoiceDictation({
        onResult: (text) => onChange({ target: { value: text } }),
        onError: (message) => toast.info(message),
    });
    useEffect(() => {
        if (dictation.state !== 'listening') return;
        const onKey = (e) => { if (e.key === 'Escape') dictation.stop(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [dictation]);
    const searchInputRef = useRef(null);
    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
            const el = e.target;
            if (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)) return;
            const input = searchInputRef.current;
            if (!input || input.offsetParent === null) return;
            e.preventDefault();
            input.focus();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, []);
    // a click on the bar's empty space goes to the search field (the bar *is* the search)
    const focusSearch = (e) => {
        if (e.target !== e.currentTarget && !e.target.classList.contains('table-toolbar__spacer')) return;
        e.preventDefault();
        searchInputRef.current?.focus();
    };

    return (
        <div className="table-toolbar" onMouseDown={focusSearch} style={{
            cursor: 'text',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '8px 16px', // compact bar
            borderBottom: '1px solid #f0f1f3',
            background: '#fff',
        }}>
            {/* Search input */}
            <div className="table-toolbar__search" style={{ position: 'relative', flex: '1 1 auto', maxWidth: searchMaxWidth }}>
                <FontAwesomeIcon
                    icon={faMagnifyingGlass}
                    style={{
                        position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                        fontSize: 14, color: '#64748b', pointerEvents: 'none', zIndex: 1, // same size as the text
                    }}
                />
                <input
                    type="text"
                    ref={searchInputRef}
                    className="form-control table_search_input"
                    placeholder={placeholder}
                    value={value}
                    onChange={onChange}
                    autoFocus={autoFocusSearch}
                    style={{
                        height: 34, fontSize: 14,
                        borderRadius: 8,
                        paddingLeft: 36,
                        paddingRight: 10 + countWidth + clearBtnSpace,
                    }}
                />
                <span
                    ref={countRef}
                    className="table-toolbar__count"
                    style={{
                        position: 'absolute', right: 10 + clearBtnSpace, top: '50%', transform: 'translateY(-50%)',
                        fontSize: 12, color: '#9ca3af', whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 1,
                    }}
                >
                    {/* result count as discreet text, numbers grouped by thousands (5 286) */}
                    <span className="table-toolbar__count-text">
                        <strong>{Number(loadedCount || 0).toLocaleString('fr-FR')}</strong>
                        <span className="table-toolbar__count-sep">/</span>
                        <strong>{Number(totalElements || 0).toLocaleString('fr-FR')}</strong>
                        <span className="table-toolbar__count-label">{totalElements !== 1 ? t('results') : t('result')}</span>
                    </span>
                </span>
                {value && (
                    // same icon-only square as the toolbar buttons next to it (gray icon, .toolbar-btn),
                    // on a light gray fill (--filled), sized to fit inside the field
                    <button
                        type="button"
                        className="toolbar-btn toolbar-btn--filled"
                        aria-label={t('Clear')}
                        onClick={() => onChange({ target: { value: '' } })}
                        style={{
                            position: 'absolute', right: 3, top: '50%', transform: 'translateY(-50%)',
                            width: 28, height: 28,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: 'transparent', border: 'none', borderRadius: 7,
                            cursor: 'pointer', color: '#64748b',
                            padding: 0, lineHeight: 1, zIndex: 2,
                        }}
                    >
                        <FontAwesomeIcon icon={faXmark} style={{ fontSize: 13.5 }} />
                    </button>
                )}
            </div>

            {counterExtra && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    <span style={{ width: 1, height: 14, background: '#e5e7eb' }} />
                    {counterExtra}
                </span>
            )}

            <div className="table-toolbar__spacer" style={{ flex: searchMaxWidth ? '1 1 auto' : '0 0 0' }} />

            {/* voice search: a toolbar button like the others, right after the search + count */}
            {voiceEnabled && (
                <button
                    type="button"
                    className={`toolbar-btn table-toolbar__mic table-toolbar__mic--${dictation.state}`}
                    onClick={() => (dictation.state === 'idle' ? dictation.start() : dictation.stop())}
                    disabled={dictation.state === 'transcribing'}
                    aria-label={dictation.state === 'listening' ? t('voice_search.stop') : t('voice_search.start')}
                    data-tooltip-id="tooltip"
                    data-tooltip-content={dictation.state === 'listening' ? t('voice_search.stop') : t('voice_search.start')}
                    // mic level drives the pulse ring while listening
                    style={{ '--mic-level': dictation.level }}
                >
                    <FontAwesomeIcon
                        icon={dictation.state === 'transcribing' ? faSpinner : faMicrophone}
                        spin={dictation.state === 'transcribing'}
                    />
                </button>
            )}

            {viewToggle && <div className="table-toolbar__view-toggle">{viewToggle}</div>}

            {toolbarExtra && (
                <div className="table-toolbar__extra" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {toolbarExtra}
                </div>
            )}

            {/* Toolbar buttons */}
            <div className="table-toolbar__buttons" style={{ display: 'flex', alignItems: 'center' }}>

                {/* ── Vues ── */}
                {showViewsButton && onApplyView && (
                    <ToolbarBtn
                        icon={faBookmark}
                        label={activeViewId ? (views.find(v => v.id === activeViewId)?.name ?? t('Views')) : t('Views')}
                        active={!!activeViewId}
                        dropdownWidth={260}
                    >
                        <div style={{ padding: '14px 12px 10px' }}>
                            <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9ca3af', margin: '0 0 10px', paddingLeft: 4 }}>
                                {t('Views')}
                            </p>
                            <ViewsPanel
                                views={views}
                                viewsLoading={viewsLoading}
                                activeViewId={activeViewId}
                                onApplyView={onApplyView}
                                onCreateView={onCreateView}
                                onDeleteView={onDeleteView}
                                onResetView={onResetView}
                                onRenameView={onRenameView}
                            />
                        </div>
                    </ToolbarBtn>
                )}

                {/* ── Filtres ── */}
                {filterableCount > 0 && (
                    <ToolbarBtn
                        icon={faFilter}
                        label={hasActiveFilters ? t('Filters (active)') : t('Filters')}
                        active={hasActiveFilters}
                        backdrop
                        dropdownWidth={filterableCount > MANY_FILTERS_THRESHOLD ? Math.min(800, window.innerWidth - 16) : 540}
                    >
                        {(close) => (
                        <div style={{ padding: '14px 16px 12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 12px' }}>
                                <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9ca3af', margin: 0 }}>
                                    {t('Filters')}
                                </p>
                                <button
                                    type="button"
                                    className="toolbar-dropdown__close"
                                    onClick={close}
                                    aria-label={t('Close')}
                                    data-tooltip-id="tooltip"
                                    data-tooltip-content={t('Close')}
                                >
                                    <FontAwesomeIcon icon={faXmark} />
                                </button>
                            </div>
                            <TableFilters
                                // TableFilters' form only reads defaultValues once, at mount (react-hook-form
                                // doesn't reactively resync) — remount it when a saved view is applied (the one
                                // case filters change from outside the form itself) so the inputs reflect it.
                                key={activeViewId ?? 'default'}
                                columns={columns}
                                onFilter={onFilter}
                                onReset={onReset}
                                defaultValues={filters}
                            />
                        </div>
                        )}
                    </ToolbarBtn>
                )}

                {/* ── Colonnes ── */}
                {showColumnsButton && (
                    <ToolbarBtn icon={faColumns} label={t('Columns')} dropdownWidth={240}>
                        <div style={{ padding: '14px 12px 10px' }}>
                            <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9ca3af', margin: '0 0 10px', paddingLeft: 4 }}>
                                {t('Visible columns')}
                            </p>
                            <ColumnsList visibleColumns={visibleColumns} setVisibleColumns={setVisibleColumns} />
                        </div>
                        {onResetColumns && (
                            <div className="table-columns-reset">
                                <button type="button" onClick={onResetColumns}>
                                    <FontAwesomeIcon icon={faRotateLeft} />
                                    {t('Reset columns')}
                                </button>
                            </div>
                        )}
                    </ToolbarBtn>
                )}

                {/* ── Exporter ── */}
                {showExportButton && (
                    <ToolbarBtn icon={faDownload} label={t('Export')} disabled={!onExport} dropdownWidth={210}>
                        <div style={{ padding: '8px 0' }}>
                            <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9ca3af', margin: '4px 16px 8px' }}>
                                {t('Format')}
                            </p>
                            <ExportRow icon={faFilePdf}   color="#dc2626" label="PDF"   onClick={() => onExport('pdf')} />
                            <ExportRow icon={faFileExcel} color="#16a34a" label="Excel" onClick={() => onExport('xlsx')} />
                            <ExportRow icon={faFileCsv}   color="#374151" label="CSV"   onClick={() => onExport('csv')} />
                            <div style={{ height: 1, background: '#f0f1f3', margin: '6px 0' }} />
                            <ExportRow icon={faAt} color="#6b7280" label={t('Send by email')} onClick={() => onExport('email')} />
                        </div>
                    </ToolbarBtn>
                )}
            </div>
        </div>
    );
};

export default TableSearch;
